import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { ProTip, Warning } from '../../components/LessonUI';

const HTMLImages = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Images & Graphics</div>
                <h1>Images & Image Tags</h1>
                <p className="hero-lead">
                    Master the img element and display images on the web. Learn about image formats,
                    alt text, responsive images, and optimization techniques for better performance.
                </p>
            </div>

            <div className="content-section">
                <h2>The &lt;img&gt; Element</h2>
                <p>
                    The <code>&lt;img&gt;</code> element embeds images into web pages. It's a self-closing (void)
                    element that requires the <code>src</code> attribute.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Basic Image Syntax</span>
                    </div>
                    <CodeBlock language="html" code={`<img src="path/to/image.jpg" alt="Description of image">`} />
                </div>

                <div className="syntax-breakdown">
                    <h3>Required Attributes:</h3>
                    <ul>
                        <li><code>src</code> - Source URL of the image (required)</li>
                        <li><code>alt</code> - Alternative text description (required for accessibility)</li>
                    </ul>
                </div>
            </div>

            <div className="content-section">
                <h2>Essential Image Attributes</h2>

                <h3>1. src (Source)</h3>
                <p>Specifies the path to the image file:</p>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Relative path --&gt;
<img src="images/photo.jpg" alt="Photo">

<!-- Root-relative path --&gt;
<img src="/assets/images/logo.png" alt="Logo">

<!-- Absolute URL --&gt;
<img src="https://example.com/images/banner.jpg" alt="Banner">

<!-- Data URL (base64 encoded) --&gt;
<img src="data:image/png;base64,iVBORw0KGg..." alt="Inline image">`} />
                </div>

                <h3>2. alt (Alternative Text)</h3>
                <p>Critical for accessibility and SEO:</p>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Descriptive alt text --&gt;
<img src="golden-retriever.jpg" alt="Golden retriever playing fetch in park">

<!-- Decorative image (empty alt) --&gt;
<img src="decorative-divider.png" alt="">

<!-- Logo --&gt;
<img src="company-logo.png" alt="Acme Corporation">

<!-- Icon with context --&gt;
<img src="checkmark.svg" alt="Success">`} />
                </div>

                <div className="alt-text-guide">
                    <h4>Writing Good Alt Text:</h4>
                    <div className="best-practices-grid">
                        <div className="practice-card">
                            <h3>Do</h3>
                            <ul>
                                <li>Be descriptive and specific</li>
                                <li>Keep it concise (under 125 characters)</li>
                                <li>Describe content, not "image of" or "picture of"</li>
                                <li>Use empty alt="" for decorative images</li>
                                <li>Include text in image if relevant</li>
                                <li>Provide context for function/purpose</li>
                            </ul>
                        </div>
                        <div className="practice-card error">
                            <h3> Don't</h3>
                            <ul>
                                <li>Leave alt attribute out entirely</li>
                                <li>Use "image" or "photo" in description</li>
                                <li>Be vague ("dog" vs "golden retriever")</li>
                                <li>Write essays (keep it brief)</li>
                                <li>Include "image of" or "picture of"</li>
                                <li>Use filename as alt text</li>
                            </ul>
                        </div>
                    </div>
                </div>

                <h3>3. width and height</h3>
                <p>Specify image dimensions to prevent layout shift:</p>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Explicit dimensions --&gt;
<img src="photo.jpg" alt="Photo" width="800" height="600">

<!-- Aspect ratio preserved --&gt;
<img src="photo.jpg" alt="Photo" width="400" height="300">

<!-- In CSS (preferred for responsive) --&gt;
<img src="photo.jpg" alt="Photo" style="width: 100%; height: auto;">`} />
                </div>

                <ProTip><div className="info-icon"></div>
                    <div className="info-text">
                        <strong>Why Specify Dimensions:</strong>
                        <ul>
                            <li>Prevents layout shift as page loads (better CLS score)</li>
                            <li>Browser reserves space before image loads</li>
                            <li>Improves user experience</li>
                            <li>Better Core Web Vitals scores</li>
                        </ul>
                    </div></ProTip>

                <h3>4. loading (Lazy Loading)</h3>
                <p>Defer loading images until needed:</p>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Load immediately (default) --&gt;
<img src="hero.jpg" alt="Hero" loading="eager">

<!-- Lazy load (below fold) --&gt;
<img src="photo1.jpg" alt="Photo" loading="lazy">
<img src="photo2.jpg" alt="Photo" loading="lazy">
<img src="photo3.jpg" alt="Photo" loading="lazy">`} />
                </div>

                <div className="loading-comparison">
                    <table>
                        <thead>
                            <tr>
                                <th>Value</th>
                                <th>When to Use</th>
                                <th>Behavior</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><code>eager</code></td>
                                <td>Above-the-fold images</td>
                                <td>Load immediately (default)</td>
                            </tr>
                            <tr>
                                <td><code>lazy</code></td>
                                <td>Below-the-fold images</td>
                                <td>Load when near viewport</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <h3>5. title (Tooltip)</h3>
                <p>Shows on hover (use sparingly):</p>
                <div className="code-example">
                    <CodeBlock language="html" code={`<img src="icon.png" alt="Help" title="Click for more information">`} />
                </div>

                <Warning><strong> Don't rely on title:</strong> Many users won't see tooltips (mobile,
                    keyboard users, screen readers). Use alt text for important information.</Warning>
            </div>

            <div className="content-section">
                <h2>Image Formats</h2>
                <p>
                    Choose the right format for your needs:
                </p>

                <div className="formats-grid">
                    <div className="format-card">
                        <h3> JPEG (.jpg, .jpeg)</h3>
                        <ul>
                            <li><strong>Best for:</strong> Photographs, complex images</li>
                            <li><strong>Pros:</strong> Small file sizes, good compression</li>
                            <li><strong>Cons:</strong> Lossy compression, no transparency</li>
                            <li><strong>Use when:</strong> Photos with many colors</li>
                        </ul>
                    </div>

                    <div className="format-card">
                        <h3> PNG (.png)</h3>
                        <ul>
                            <li><strong>Best for:</strong> Graphics, logos, icons</li>
                            <li><strong>Pros:</strong> Lossless, supports transparency</li>
                            <li><strong>Cons:</strong> Larger file sizes than JPEG</li>
                            <li><strong>Use when:</strong> Need transparency or sharp edges</li>
                        </ul>
                    </div>

                    <div className="format-card">
                        <h3> WebP (.webp)</h3>
                        <ul>
                            <li><strong>Best for:</strong> Modern web (all purposes)</li>
                            <li><strong>Pros:</strong> Best compression, transparency support</li>
                            <li><strong>Cons:</strong> Limited older browser support</li>
                            <li><strong>Use when:</strong> Targeting modern browsers</li>
                        </ul>
                    </div>

                    <div className="format-card">
                        <h3> SVG (.svg)</h3>
                        <ul>
                            <li><strong>Best for:</strong> Icons, logos, illustrations</li>
                            <li><strong>Pros:</strong> Scalable, small file size, crisp at any size</li>
                            <li><strong>Cons:</strong> Not suitable for photos</li>
                            <li><strong>Use when:</strong> Vector graphics, need scaling</li>
                        </ul>
                    </div>

                    <div className="format-card">
                        <h3> GIF (.gif)</h3>
                        <ul>
                            <li><strong>Best for:</strong> Simple animations</li>
                            <li><strong>Pros:</strong> Supports animation, wide support</li>
                            <li><strong>Cons:</strong> Limited colors (256), large files</li>
                            <li><strong>Use when:</strong> Simple animations (prefer video)</li>
                        </ul>
                    </div>

                    <div className="format-card">
                        <h3> AVIF (.avif)</h3>
                        <ul>
                            <li><strong>Best for:</strong> Next-gen compression</li>
                            <li><strong>Pros:</strong> Best compression, high quality</li>
                            <li><strong>Cons:</strong> New format, limited support</li>
                            <li><strong>Use when:</strong> Progressive enhancement</li>
                        </ul>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>Responsive Images</h2>
                <p>
                    Serve different image sizes based on screen size and resolution:
                </p>

                <h3>CSS-Only Responsive Images</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<img src="photo.jpg" alt="Photo" style="max-width: 100%; height: auto;">

<style>
img {
    max-width: 100%;
    height: auto;
}
</style>`} />
                </div>

                <h3>Using srcset (Resolution Switching)</h3>
                <p>Serve different resolutions for different pixel densities:</p>
                <div className="code-example">
                    <CodeBlock language="html" code={`<img 
    src="image-800w.jpg" 
    srcset="image-400w.jpg 400w,
            image-800w.jpg 800w,
            image-1200w.jpg 1200w"
    sizes="(max-width: 600px) 400px,
           (max-width: 1000px) 800px,
           1200px"
    alt="Responsive image"
>`} />
                </div>

                <div className="how-it-works">
                    <h4>How srcset Works:</h4>
                    <ol>
                        <li><code>srcset</code> lists available image files with their widths</li>
                        <li><code>sizes</code> tells browser what size the image will be displayed at</li>
                        <li>Browser chooses best image based on viewport size and pixel density</li>
                        <li><code>src</code> is fallback for older browsers</li>
                    </ol>
                </div>

                <h3>Using &lt;picture&gt; Element (Art Direction)</h3>
                <p>Serve completely different images for different contexts:</p>
                <div className="code-example">
                    <CodeBlock language="html" code={`<picture>
    <!-- Mobile: Portrait crop --&gt;
    <source media="(max-width: 600px)" srcset="image-mobile.jpg">
    
    <!-- Tablet: Square crop --&gt;
    <source media="(max-width: 1000px)" srcset="image-tablet.jpg">
    
    <!-- Desktop: Landscape crop --&gt;
    <source media="(min-width: 1001px)" srcset="image-desktop.jpg">
    
    <!-- Fallback --&gt;
    <img src="image-desktop.jpg" alt="Responsive image">
</picture>`} />
                </div>

                <h3>Modern Format Fallbacks</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<picture>
    <!-- Try AVIF (best compression) --&gt;
    <source type="image/avif" srcset="image.avif">
    
    <!-- Try WebP (good compression) --&gt;
    <source type="image/webp" srcset="image.webp">
    
    <!-- Fallback to JPEG (universal support) --&gt;
    <img src="image.jpg" alt="Image">
</picture>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Image Optimization</h2>

                <div className="optimization-checklist">
                    <h3>Optimization Checklist:</h3>
                    <ul>
                        <li> <strong>Choose right format:</strong> JPEG for photos, PNG for graphics, WebP for modern web</li>
                        <li> <strong>Compress images:</strong> Use tools like TinyPNG, ImageOptim, Squoosh</li>
                        <li> <strong>Serve correctly sized images:</strong> Don't serve 4000px image for 400px display</li>
                        <li> <strong>Use lazy loading:</strong> Add <code>loading="lazy"</code> for below-fold images</li>
                        <li> <strong>Specify dimensions:</strong> Always include width and height</li>
                        <li> <strong>Use CDN:</strong> Serve images from CDN for faster delivery</li>
                        <li> <strong>Enable caching:</strong> Set proper cache headers</li>
                        <li> <strong>Use responsive images:</strong> Implement srcset for multiple sizes</li>
                    </ul>
                </div>

                <div className="code-example">
                    <div className="code-header">
                        <span>Optimized Image Example</span>
                    </div>
                    <CodeBlock language="html" code={`<picture>
    <!-- WebP for modern browsers --&gt;
    <source 
        type="image/webp" 
        srcset="photo-400.webp 400w,
                photo-800.webp 800w,
                photo-1200.webp 1200w"
        sizes="(max-width: 600px) 400px,
               (max-width: 1000px) 800px,
               1200px"
    >
    
    <!-- JPEG fallback --&gt;
    <img 
        src="photo-800.jpg"
        srcset="photo-400.jpg 400w,
                photo-800.jpg 800w,
                photo-1200.jpg 1200w"
        sizes="(max-width: 600px) 400px,
               (max-width: 1000px) 800px,
               1200px"
        alt="Beautiful landscape"
        width="1200"
        height="800"
        loading="lazy"
    >
</picture>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Styling Images with CSS</h2>

                <div className="code-example">
                    <div className="code-header">
                        <span>Common Image Styles</span>
                    </div>
                    <CodeBlock language="css" code={`/* Basic responsive */
img {
    max-width: 100%;
    height: auto;
}

/* Rounded corners */
img.rounded {
    border-radius: 10px;
}

/* Circle (for avatars) */
img.circle {
    border-radius: 50%;
    width: 100px;
    height: 100px;
    object-fit: cover;
}

/* Shadow */
img.shadow {
    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

/* Grayscale filter */
img.grayscale {
    filter: grayscale(100%);
}

img.grayscale:hover {
    filter: grayscale(0%);
    transition: filter 0.3s;
}

/* Object-fit (like background-size) */
img.cover {
    width: 100%;
    height: 300px;
    object-fit: cover; /* Fill container, crop if needed */
}

img.contain {
    width: 100%;
    height: 300px;
    object-fit: contain; /* Fit inside, show all */
}

/* Border */
img.framed {
    border: 5px solid white;
    box-shadow: 0 2px 10px rgba(0,0,0,0.2);
}`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Figure and Figcaption</h2>
                <p>
                    Semantic way to associate images with captions:
                </p>

                <div className="code-example">
                    <CodeBlock language="html" code={`<figure>
    <img src="photo.jpg" alt="Mountain landscape">
    <figcaption>Rocky Mountains at sunset, Colorado</figcaption>
</figure>

<style>
figure {
    margin: 2rem 0;
    text-align: center;
}

figure img {
    max-width: 100%;
    height: auto;
}

figcaption {
    margin-top: 0.5rem;
    font-size: 0.9rem;
    color: #666;
    font-style: italic;
}
</style>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Decorative vs. Meaningful Images</h2>

                <div className="comparison-grid">
                    <div className="comparison-card">
                        <h3>Meaningful Images</h3>
                        <p>Convey information or content:</p>
                        <div className="code-example">
                            <CodeBlock language="html" code={`<!-- Product photo --&gt;
<img src="laptop.jpg" alt="15-inch MacBook Pro">

<!-- Infographic --&gt;
<img src="chart.png" alt="Sales increased 45% in Q4">

<!-- Tutorial screenshot --&gt;
<img src="step1.png" alt="Click the File menu">`} />
                        </div>
                    </div>

                    <div className="comparison-card">
                        <h3>Decorative Images</h3>
                        <p>Purely visual, no information:</p>
                        <div className="code-example">
                            <CodeBlock language="html" code={`<!-- Decorative border --&gt;
<img src="divider.png" alt="">

<!-- Background pattern --&gt;
<img src="pattern.png" alt="">

<!-- Decorative icon (when text explains) --&gt;
<img src="icon.svg" alt="">
<p>Download our app</p>`} />
                        </div>
                    </div>
                </div>

                <div className="tip-box">
                    <strong> Rule of Thumb:</strong> If removing the image would lose information,
                    it's meaningful (needs descriptive alt text). If it's just for decoration, use empty alt="".
                </div>
            </div>

            <div className="content-section">
                <h2>Common Image Mistakes</h2>

                <div className="mistakes-grid">
                    <div className="mistake-card">
                        <h3> Missing Alt Text</h3>
                        <div className="code-example error">
                            <CodeBlock language="html" code={`<img src="photo.jpg">  <!-- Wrong! --&gt;`} />
                        </div>
                        <div className="code-example">
                            <CodeBlock language="html" code={`<img src="photo.jpg" alt="Description">  <!-- Correct --&gt;`} />
                        </div>
                    </div>

                    <div className="mistake-card">
                        <h3> Using Image for Text</h3>
                        <div className="code-example error">
                            <CodeBlock language="html" code={`<img src="heading.png" alt="Welcome">  <!-- Bad for SEO/accessibility --&gt;`} />
                        </div>
                        <div className="code-example">
                            <CodeBlock language="html" code={`<h1>Welcome</h1>  <!-- Use real text --&gt;`} />
                        </div>
                    </div>

                    <div className="mistake-card">
                        <h3> Huge Unoptimized Images</h3>
                        <div className="code-example error">
                            <CodeBlock language="html" code={`<!-- 5MB image for 300px display --&gt;
<img src="photo-4000x3000.jpg" width="300">`} />
                        </div>
                        <div className="code-example">
                            <CodeBlock language="html" code={`<!-- Properly sized and compressed --&gt;
<img src="photo-600x450.jpg" width="300" loading="lazy">`} />
                        </div>
                    </div>

                    <div className="mistake-card">
                        <h3> Not Specifying Dimensions</h3>
                        <div className="code-example error">
                            <CodeBlock language="html" code={`<img src="photo.jpg" alt="Photo">  <!-- Causes layout shift --&gt;`} />
                        </div>
                        <div className="code-example">
                            <CodeBlock language="html" code={`<img src="photo.jpg" alt="Photo" width="800" height="600">  <!-- Better --&gt;`} />
                        </div>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>Best Practices Summary</h2>
                <div className="best-practices-grid">
                    <div className="practice-card">
                        <h3>Do</h3>
                        <ul>
                            <li>Always include alt text</li>
                            <li>Optimize images before uploading</li>
                            <li>Specify width and height</li>
                            <li>Use lazy loading for below-fold images</li>
                            <li>Choose appropriate file format</li>
                            <li>Implement responsive images</li>
                            <li>Use modern formats with fallbacks</li>
                            <li>Test images on slow connections</li>
                        </ul>
                    </div>
                    <div className="practice-card error">
                        <h3> Don't</h3>
                        <ul>
                            <li>Forget alt attributes</li>
                            <li>Serve oversized images</li>
                            <li>Use images for text content</li>
                            <li>Ignore mobile optimization</li>
                            <li>Use poor quality images</li>
                            <li>Forget about loading performance</li>
                            <li>Use spacer.gif (deprecated)</li>
                            <li>Hotlink images from other sites</li>
                        </ul>
                    </div>
                </div>
            </div>

        </div>
    );
};

export default HTMLImages;



